import { api } from '@/utils/api';
import { useToast } from '@/hooks/use-toast';
import { IonButton, IonFab, IonFabButton, IonIcon, IonInfiniteScroll, IonInfiniteScrollContent } from '@ionic/react';
import { add, checkmarkDoneOutline, refreshOutline } from 'ionicons/icons';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { Post } from './Post';
import { Spinner } from './ui/spinner';
import FriendsRecommendation from './FriendsRecommendation';
import { WhatsUp } from './WhatsUp';
import { postEventService } from '@/services/PostEventService';

interface FollowedPostsProps {
  profile: any;
  handleDeletePost: (postId: number) => Promise<any>;
  pageRef: React.RefObject<HTMLElement>;
  addPostToFeed?: (newPost: any) => void;
}

const FollowedPosts: React.FC<FollowedPostsProps> = ({
  profile,
  handleDeletePost,
  pageRef,
  addPostToFeed
}) => {
  // State for followed posts
  const [posts, setPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [fetchError, setFetchError] = useState(false);

  // No longer need observer ref since we're using IonInfiniteScroll
  const { toast } = useToast();
  const { t } = useTranslation();

  // Fetch followed posts (initial or paginated)
  const fetchPosts = useCallback(
    async (isInitial: boolean = false) => {
      if (!profile?.id) return;

      if (isInitial) {
        setLoading(true);
        setPage(1);
      } else {
        setLoadingMore(true);
      }

      try {
        const currentPage = isInitial ? 1 : page;
        let apiUrl = `/posts?page=${currentPage}&limit=10&type=followed&profileId=${profile.id}`;

        const { data: { posts: newPosts, metadata } } = await api.get(apiUrl);
        const hasNewPosts = newPosts && newPosts.length > 0;

        if (isInitial) {
          setPosts(newPosts);
        } else if (hasNewPosts) {
          // Filter out any duplicates before adding new posts
          setPosts(prevPosts => {
            const existingPostIds = new Set(prevPosts.map((post: any) => post.id));
            const uniqueNewPosts = newPosts.filter((post: any) => !existingPostIds.has(post.id));

            if (uniqueNewPosts.length === 0) {
              // If all posts were duplicates, we've reached the end
              setHasMore(false);
              return prevPosts;
            }

            return [...prevPosts, ...uniqueNewPosts];
          });
        }

        // Check if we've reached the end of available posts
        const reachedEnd = !hasNewPosts || (newPosts.length < 10) || (currentPage >= metadata.totalPages);

        if (reachedEnd) {
          console.log('Reached end of content');
          setHasMore(false);
        } else {
          setHasMore(true);
        }

        // Only increment page if we actually got new posts and aren't at the end
        if (!isInitial && hasNewPosts && !reachedEnd) {
          setPage(prevPage => prevPage + 1);
        }
      } catch (error) {
        console.error('Error fetching followed posts:', error);
        Sentry.captureException(error);
        setFetchError(true);

        toast({
          description: t('cant_fetch_posts'),
          variant: 'destructive'
        });
      } finally {
        if (isInitial) {
          setLoading(false);
        } else {
          setLoadingMore(false);
        }
      }
    },
    [page, profile, toast, t],
  );

  // Load more posts with additional checks to prevent duplicate loads
  const loadMorePosts = useCallback(() => {
    if (!loadingMore && hasMore && profile?.id && posts.length > 0) {
      console.log(`Loading more posts, current page: ${page}`);
      fetchPosts(false);
    } else if (!hasMore && posts.length > 0) {
      console.log('No more posts to load');
    }
  }, [loadingMore, hasMore, fetchPosts, profile, page, posts.length]);

  // Handle infinite scroll event from IonInfiniteScroll
  const handleInfiniteScroll = useCallback((event: CustomEvent<void>) => {
    if (hasMore && !loadingMore) {
      console.log('Loading more followed posts from IonInfiniteScroll');
      loadMorePosts();
    }

    // Complete the infinite scroll with a slight delay to ensure UI updates
    setTimeout(() => {
      // Properly type the event target as HTMLIonInfiniteScrollElement
      const infiniteScroll = event.target as HTMLIonInfiniteScrollElement;
      infiniteScroll.complete();

      // Disable the infinite scroll if we've reached the end
      if (!hasMore) {
        console.log('No more posts to load, disabling infinite scroll');
        infiniteScroll.disabled = true;
      }
    }, 500);
  }, [hasMore, loadingMore, loadMorePosts]);

  // Fetch new posts for pull-to-refresh
  const fetchNewPosts = useCallback(async () => {
    if (!profile || posts.length === 0) return;

    try {
      const latestPostTimestamp = posts[0]?.createdAt;
      const apiUrl = `/posts?since=${latestPostTimestamp}&type=followed&profileId=${profile.id}`;

      const { data: { posts: newPosts } } = await api.get(apiUrl);
      if (newPosts && newPosts.length > 0) {
        setPosts(prevPosts => {
          const existingPostIds = new Set(prevPosts.map((post: any) => post.id));
          const uniqueNewPosts = newPosts.filter((post: any) => !existingPostIds.has(post.id));
          return [...uniqueNewPosts, ...prevPosts];
        });

        // Show success message for new posts
        toast({
          description: newPosts.length > 1
            ? t('new_posts_loaded', { count: newPosts.length })
            : t('new_post_loaded'),
          variant: 'default'
        });
      } else {
        // Show message that we're already at the latest posts
        toast({
          description: t('no_new_posts'),
          variant: 'default'
        });
      }
    } catch (error) {
      console.error('Error fetching new posts:', error);
      toast({
        description: t('cant_fetch_posts'),
        variant: 'destructive'
      });
    }
  }, [posts, profile, toast, t]);

  // Function for pull-to-refresh is exposed via window object
  // No need for a separate handlePullToRefresh function

  // Export the function for parent component to use
  useEffect(() => {
    // Make the function available on the component instance
    if (typeof window !== 'undefined') {
      (window as any).followedPostsRefresh = fetchNewPosts;
    }

    return () => {
      // Clean up when component unmounts
      if (typeof window !== 'undefined') {
        delete (window as any).followedPostsRefresh;
      }
    };
  }, [fetchNewPosts]);

  // Function to retry fetching posts
  const retryFetchPosts = () => {
    setFetchError(false);
    console.log('retry fetch')
    fetchPosts(true);
  };

  // Use the passed in addPostToFeed or create a local one
  const handleAddPost = useCallback((newPost: any) => {
    // Make sure we have a valid post before adding it
    if (newPost && newPost.id) {
      setPosts(prevPosts => [newPost, ...prevPosts]);
      // If parent provided an addPostToFeed function, call it too
      if (addPostToFeed) {
        addPostToFeed(newPost);
      }
    }
  }, [addPostToFeed]);

  // Ref to track if we've already loaded posts to prevent multiple initial loads
  const hasLoadedRef = useRef(false);

  // Initial load - only run once when profile is available
  useEffect(() => {
    if (profile?.id && !hasLoadedRef.current) {
      console.log("Loading posts for the first time");
      fetchPosts(true);
      hasLoadedRef.current = true;
    }
  }, [profile?.id, fetchPosts]);

  // Listen for post created events
  useEffect(() => {
    // Add event listener for post created events
    const handlePostCreated = (post: any) => {
      console.log('Post created event received in FollowedPosts:', post);
      handleAddPost(post);
    };

    postEventService.addEventListener('postCreated', handlePostCreated);

    // Clean up the event listener when the component unmounts
    return () => {
      postEventService.removeEventListener('postCreated', handlePostCreated);
    };
  }, [handleAddPost]);

  // Reset infinite scroll when posts change
  useEffect(() => {
    // Re-enable infinite scroll when we get new posts and still have more to load
    const infiniteScroll = document.getElementById('followed-infinite-scroll') as HTMLIonInfiniteScrollElement;
    if (infiniteScroll && hasMore) {
      infiniteScroll.disabled = false;
    }
  }, [posts, hasMore]);

  // Check if we're at the end of the content
  const isAtEnd = !hasMore && posts.length > 0 && !loading;

  return (
    <div className="space-y-4 sm:px-0 relative">
      <WhatsUp addPostToFeed={handleAddPost} />

      {loading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : fetchError ? (
        <div className="flex flex-col items-center justify-center gap-4 py-8 px-4 text-center">
          <IonButton
            color="medium"
            fill="outline"
            shape="round"
            onClick={retryFetchPosts}
          >
            <IonIcon slot="icon-only" icon={refreshOutline} />
          </IonButton>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">{t('cant_fetch_posts')}</h3>
        </div>
      ) : posts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
          <h3 className="text-lg font-medium mb-2">{t('no_followed_posts')}</h3>
          <p className="text-muted-foreground mb-4 text-sm">{t('no_followed_posts_caption')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {posts.map((post) => (
            <Post
              key={`followed-post-${post.id}`}
              id={post.id}
              user={post.user}
              media={post.media}
              likes={post.likes}
              isLiked={post.isLiked}
              course={post.course}
              caption={post.caption}
              comments={post.comments}
              audience={post.audience}
              profileId={profile.id}
              createdAt={post.createdAt}
              onDelete={handleDeletePost}
              pageRef={pageRef}
            />
          ))}

          {/* Ionic Infinite Scroll */}
          <IonInfiniteScroll
            id="followed-infinite-scroll"
            onIonInfinite={handleInfiniteScroll}
            threshold="100px"
            disabled={!hasMore || loadingMore}
          >
            <IonInfiniteScrollContent
              loadingSpinner="bubbles"
              loadingText={t('loading_more_posts')}
            ></IonInfiniteScrollContent>
          </IonInfiniteScroll>

          {/* End of content indicator at bottom */}
          {isAtEnd && (
            <div className="text-center py-4 text-sm text-muted-foreground border-gray-200 dark:border-gray-800 mt-4">
              <p>{t('no_more_posts')}</p>
              <IonIcon icon={checkmarkDoneOutline} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FollowedPosts;
